import {
  PieChart,
  buildPieChartSerie,
} from '@delmonicos/shared-design-system-react/src/components/pie-chart/pie-chart';
import {
  GapEnum,
  Stack,
} from '@delmonicos/shared-design-system-react/src/components/stack/stack';
import { ChargePointStatus } from '@delmonicos/shared-sdk/libs/cpms/src/libs/ocpp/src/enums/charge-point-status.enum';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useCpmsBoGetChannelStatusesStatisticsQuery } from '@service/cpms-bo-channels-statistics.service';

import { LegendItemComponent } from './legend/legend-item.component';
import * as css from './network-state-chart.module.scss';

export const NetworkStateChartComponent = (): React.JSX.Element => {
  const { t } = useTranslation();
  const { data } = useCpmsBoGetChannelStatusesStatisticsQuery({});

  const groupedData = useMemo(() => {
    const grouped = {
      charging: 0,
      available: 0,
      faulted: 0,
      unavailable: 0,
    };
    data?.value.forEach((item) => {
      switch (item.status) {
        case ChargePointStatus.Preparing:
        case ChargePointStatus.SuspendedEVSE:
        case ChargePointStatus.SuspendedEV:
        case ChargePointStatus.Finishing:
        case ChargePointStatus.Charging:
          grouped.charging += item.total;
          break;
        case ChargePointStatus.Available:
          grouped.available += item.total;
          break;
        case ChargePointStatus.Faulted:
          grouped.faulted += item.total;
          break;
        case ChargePointStatus.Unavailable:
          grouped.unavailable += item.total;
          break;
        default:
          break;
      }
    });
    return grouped;
  }, [data]);

  const serie = useMemo(
    () =>
      buildPieChartSerie({
        items: [
          {
            value: groupedData.available,
            name: t(
              'delmonicos-shared-sdk.ocpp-enums-charge-point-status.Available',
            ),
            color: 'var(--brand-decorative-bis-accent)',
          },
          {
            value: groupedData.charging,
            name: t(
              'delmonicos-shared-sdk.ocpp-enums-charge-point-status.Charging',
            ),
            color: 'var(--text-charging)',
          },
          {
            value: groupedData.unavailable,
            name: t(
              'delmonicos-shared-sdk.ocpp-enums-charge-point-status.Unavailable',
            ),
            color: 'var(--surface-disabled)',
          },
          {
            value: groupedData.faulted,
            name: t(
              'delmonicos-shared-sdk.ocpp-enums-charge-point-status.Faulted',
            ),
            color: 'var(--icon-error)',
          },
        ],
        getName: (item) => item.name,
        getValue: (item) => item.value,
        getColor: (item) => item.color as any,
        othersLabel: t('$.statistics.cards.by-third-party-endpoint.others'),
      }),
    [
      groupedData.available,
      groupedData.charging,
      groupedData.faulted,
      groupedData.unavailable,
      t,
    ],
  );

  return (
    <>
      <PieChart serie={serie} />
      <Stack direction="column" gap={GapEnum.M} className={css.legend}>
        <LegendItemComponent
          status={ChargePointStatus.Available}
          value={groupedData.available}
        />
        <LegendItemComponent
          status={ChargePointStatus.Faulted}
          value={groupedData.faulted}
        />
        <LegendItemComponent
          status={ChargePointStatus.Charging}
          value={groupedData.charging}
        />
        <LegendItemComponent
          status={ChargePointStatus.Unavailable}
          value={groupedData.unavailable}
        />
      </Stack>
    </>
  );
};
